import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import PropTypes from 'prop-types'
import ActionCable from 'actioncable'

import { baseURLCable } from '~/config/cable'
import STORAGE_KEYS from '~/constants/localStorage'
import { CHANNEL_SOCKET, EVENT_CODE } from '~/constants/socket'
import { path } from '~/routes/path'
import {
  addBuffetRequestTime,
  fetchDetailRestaurant,
  selectRestaurant,
} from '~/redux/restaurantSlice'
import { checkUrlMatch } from '~/utils/handleURL'
import { getBadgeNotificationApi } from '~/apis/notification'
import { SERVING_REQUEST_STATUS } from '~/constants'
import { getInfoRestaurant } from '~/utils'
import PopupInputName from '~/components/PopupInputName'
import PopupInputPhone from '~/components/PopupInputPhone'
import PopupRecommendMenu from '~/components/PopupRecommendMenu'
import PopupStartBuffet from '~/components/PopupStartBuffet'
import PopupAddFriendLine from '~/components/PopupAddFriendLine'

const consumerServingToken = ActionCable.createConsumer(baseURLCable)
const consumerServingSession = ActionCable.createConsumer(baseURLCable)

export const AppContext = React.createContext({
  saveServingSessionID: (serving_session_id = -1) => {},
  responseServingRequest: '',
  setResponseServingRequest: () => {},
  responseServingToken: null,
  setResponseServingToken: () => {},
  isNotify: false,
  setIsNotify: () => {},
  handleReadNotify: () => {},
  handleOpenPopupFoodDetail: ({ idFood, isBuffet }) => {},
  handleClosePopupFoodDetail: () => {},
  foodID: null,
  isBuffetItem: false,
  heightHeaderFixed: 0,
  setHeightHeaderFixed: () => {},
  isOpenSearchTablet: false,
  setIsOpenSearchTablet: () => {},
  handleOpenRecommendMenu: () => {},
  isOpenPopupAddFriendLine: false,
})

const AppProvider = ({ children }) => {
  const navigate = useNavigate()
  const { tableUid, restaurantSlug } = useSelector(selectRestaurant) //  Truy cập trạng thái của nhà hàng trong redux
  const dispatch = useDispatch()
  const servingSessionIDFromLocal = localStorage.getItem(STORAGE_KEYS.SERVING_SESSION_ID) || ''
  const [servingSessionID, setServingSessionID] = useState(servingSessionIDFromLocal)
  const [responseServingRequest, setResponseServingRequest] = useState('')
  const [responseServingToken, setResponseServingToken] = useState(null)
  const [isNotify, setIsNotify] = useState(false)
  const currentPathname = useLocation().pathname
  const [foodID, setFoodID] = useState(null) // Lưu trữ id của món ăn khi click vào món ăn -> mở popup
  const [isBuffetItem, setIsBuffetItem] = useState(false) // Món ăn đó có thể là buffet
  const [heightHeaderFixed, setHeightHeaderFixed] = useState(0) // Lưu trữ chiều cao của header đã được tính toán dựa trên ref
  const [isOpenSearchTablet, setIsOpenSearchTablet] = useState(false)
  const [isOpenRecommend, setIsOpenRecommend] = useState(false)
  const [isOpenPopupStartBuffet, setIsOpenPopupStartBuffet] = useState(false) // Trạng thái đóng/mở popup start buffet
  const [isOpenPopupAddFriendLine, setIsOpenPopupAddFriendLine] = useState(false) // Lưu trạng thái đóng/mở popup add friend line

  // Hàm xử lý lưu serving session id vào local storage và state
  const saveServingSessionID = useCallback((serving_session_id) => {
    localStorage.setItem(STORAGE_KEYS.SERVING_SESSION_ID, serving_session_id || '')
    setServingSessionID(serving_session_id || '')
  }, [])

  // Hàm xử lý xự kiện buffet được bắt đầu
  const handleEventBuffetStarted = useCallback(
    (dataBuffet) => {
      setIsOpenPopupStartBuffet(true) // Thực hiện mở popup
      // Cập nhật lại session id để phục vụ cho việc realtime order status
      saveServingSessionID(dataBuffet.serving_session_id)
      // Thực hiện lưu thông tin buffet lên redux
      dispatch(
        addBuffetRequestTime({
          name: dataBuffet.food_item.name,
          serving_ended_at: dataBuffet.serving_ended_at,
        }),
      )
    },
    [dispatch, saveServingSessionID],
  )

  // ref channel
  const refChannelServingToken = useRef(null)
  const refChannelServingSession = useRef(null)

  // Lắn nghe sự kiện khi nhà hàng checkout.
  useEffect(() => {
    const token = localStorage.getItem(STORAGE_KEYS.servingToken)
    // Kiểm tra nếu đã có kết nối trước đó thì không kết nối lại
    if (refChannelServingToken.current || !token) return

    if (token) {
      refChannelServingToken.current = consumerServingToken.subscriptions.create(
        {
          channel: CHANNEL_SOCKET.SERVING_TOKENS,
          serving_token: token,
        },
        {
          connected: () => {
            console.log(CHANNEL_SOCKET.SERVING_TOKENS + ' connected!')
          },
          disconnected: () => {
            console.log(CHANNEL_SOCKET.SERVING_TOKENS + ' disconnected!')
          },
          received: (response) => {
            // Trường hợp checkout thì sẽ di chuyển đến màn hình review order
            if (response?.checkout) {
              navigate(path.reviewOrder)
              setFoodID(null) // Nếu đang mở popup món ăn thì cần phải đóng lại
            }

            // Áp dụng với trường hợp food court
            if (response?.refresh_status) {
              if (!checkUrlMatch([path.orderHistoryFoodCourt], currentPathname)) {
                setIsNotify(true)
              }
              setResponseServingToken({ ...response })
            }

            // Xử lý trường hợp có request buffet (Smaregi)
            if (response?.event_code === EVENT_CODE.BUFFET_REQUEST_STARTED) {
              handleEventBuffetStarted(response.data)
            }
          },
        },
      )
    }
  }, [currentPathname, handleEventBuffetStarted])

  // useEffect lắng nghe socket trong trường hợp (có serving session id hoặc không).
  // Chú ý: Nếu không có serving session id thì socket sẽ trả về một kiểu ngược lại nếu có thì sẽ socket trả về một kiểu.
  //  Nên cần kết nối nhiều lần khi serving session id thay đôi
  useEffect(() => {
    if (tableUid) {
      refChannelServingSession.current = consumerServingSession.subscriptions.create(
        {
          channel: CHANNEL_SOCKET.SERVING_SESSION,
          table_uuid: tableUid,
          serving_session_id: servingSessionID, // Khi có serving session id thì nó sẽ subcribe lại lần nữa
        },
        {
          connected: () => {
            console.log(CHANNEL_SOCKET.SERVING_SESSION + ' connected!')
          },
          disconnected: () => {
            console.log(CHANNEL_SOCKET.SERVING_SESSION + ' disconnected!')
          },
          received: (response) => {
            const data = JSON.parse(response)

            if (data.kind === SERVING_REQUEST_STATUS.call_staff) return // trong trường hợp call staff -> không làm gì

            if (data.id) {
              setResponseServingRequest(data)

              if (!checkUrlMatch([path.orderHistory], currentPathname)) {
                setIsNotify(true)
              }

              return
            }

            if (data?.change_table) {
              localStorage.setItem(STORAGE_KEYS.USER_NAME, data?.customer_name_add_by_merchant)
              const { restaurantSlug } = getInfoRestaurant()

              // Cập nhật lại thông tin dựa trên bàn mới
              dispatch(
                fetchDetailRestaurant({
                  restaurantSlug,
                  tableUid: data.destination_table_uuid,
                }),
              )
            }

            // Trường hợp merchant cập nhật tên thì phía client cũng phải cập nhật theo
            if (data?.customer_name_add_by_merchant) {
              localStorage.setItem(STORAGE_KEYS.USER_NAME, data?.customer_name_add_by_merchant)
            }

            if (data?.checkout && servingSessionID) {
              navigate(path.reviewOrder)
              setFoodID(null) // Nếu đang mở popup món ăn thì cần phải đóng lại
            }

            if (data.event_code === EVENT_CODE.A_BUFFET_WAS_CANCELLED) {
              const { restaurantSlug, tableUid } = getInfoRestaurant()

              // Gọi lại api để cập nhật thông tin mới nhất
              dispatch(
                fetchDetailRestaurant({
                  restaurantSlug,
                  tableUid,
                }),
              )
            }
          },
        },
      )
    }

    // Chỗ này không cần unsubcribe bởi vì khi có serving session id thì nó sẽ subcribe lại lần nữa. Cố tình để lại comment này để chú ý
    // return () => {
    //   if (refChannelServingSession.current) {
    //     refChannelServingSession.current.unsubscribe()
    //     refChannelServingSession.current = null
    //   }
    // }
  }, [servingSessionID, tableUid, dispatch, currentPathname])

  // Hàm xử lý đọc thông báo.
  const handleReadNotify = useCallback(() => {
    setIsNotify(false)
  }, [])

  // Gọi api check xem có thông báo hay không
  const getBadgeNotification = useCallback(() => {
    if (!restaurantSlug) return

    /**
     * Chỗ này phải setTimout vì phía BE cần api này chạy sau api current info. Nó liên quan đến việc xử lý socket của BE
     */
    setTimeout(async () => {
      const { hasNotify } = await getBadgeNotificationApi({
        restaurant_slug: restaurantSlug,
        table_uuid: tableUid,
      })
      setIsNotify(hasNotify)
    }, 1000)
  }, [restaurantSlug, tableUid])

  useEffect(() => {
    getBadgeNotification()
  }, [getBadgeNotification])

  const renderPopupInputCustomerName = () => {
    return <PopupInputName />
  }

  const renderPopupInputPhone = () => {
    return <PopupInputPhone />
  }

  // Hàm xử lý mở popup recommend menu
  const handleOpenRecommendMenu = useCallback(() => {
    setIsOpenRecommend(true)
  }, [])

  const renderPopupRecommendMenu = () => {
    if (isOpenRecommend) return <PopupRecommendMenu setOpen={setIsOpenRecommend} />
  }

  const renderPopupAddFriendLine = () => {
    return (
      <PopupAddFriendLine
        isOpen={isOpenPopupAddFriendLine}
        setIsOpen={setIsOpenPopupAddFriendLine}
      />
    )
  }

  const renderPopupStartBuffet = () => {
    return <PopupStartBuffet open={isOpenPopupStartBuffet} setOpen={setIsOpenPopupStartBuffet} />
  }

  // Hàm xử lý mở popup chi tiết món ăn
  const handleOpenPopupFoodDetail = useCallback(({ idFood, isBuffet }) => {
    setFoodID(idFood)
    if (isBuffet) setIsBuffetItem(isBuffet)
  }, [])

  // Hàm xử lý đóng popup chi tiết món ăn
  const handleClosePopupFoodDetail = useCallback(() => {
    setFoodID(null)
    setIsBuffetItem(false)
  }, [])

  const values = {
    saveServingSessionID,
    responseServingRequest,
    setResponseServingRequest,
    isNotify,
    setIsNotify,
    handleReadNotify,
    responseServingToken,
    setResponseServingToken,
    handleOpenPopupFoodDetail,
    handleClosePopupFoodDetail,
    foodID,
    isBuffetItem,
    heightHeaderFixed,
    setHeightHeaderFixed,
    isOpenSearchTablet,
    setIsOpenSearchTablet,
    handleOpenRecommendMenu,
    isOpenPopupAddFriendLine,
  }

  return (
    <AppContext.Provider value={values}>
      {children}
      {renderPopupAddFriendLine()}
      {renderPopupStartBuffet()}
      {renderPopupInputCustomerName()}
      {renderPopupInputPhone()}
      {renderPopupRecommendMenu()}
    </AppContext.Provider>
  )
}

AppProvider.propTypes = {
  children: PropTypes.node,
}

export default AppProvider

// Ở các component con sẽ dùng hook này để lấy ra value được truyền qua
export const useAppContext = () => useContext(AppContext)
